import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ProjectMember } from "domain/entities/project-member";
import { Logger } from "infra/logger/logger";
import projectMemberRepository from "../../infra/repositories/project-member-repository";
import { ProjectMemberInputFilter } from "domain/models/project-member-input-filter";


export const useProjectMembersByFilter = (filter: ProjectMemberInputFilter) => {
    const logger = Logger.getInstance();
  
    return useQuery({
      queryKey: ["projectMembers", filter],
      queryFn: async () => {
        try {
          const result = await projectMemberRepository.getAll(filter);
          return result;
        } catch (error: any) {
          if (error instanceof AxiosError) {
            logger.error(
              error.response?.data.metadata.errors[0],
              error.response?.data.metadata
            );
            throw new Error(error.response?.data.metadata.errors);
          }
          throw error;
        }
      },
      retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
      staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
      enabled: !!filter.projectId,
    });
  };


export const useCreateProjectMemberMutation = () => {
    const logger = Logger.getInstance();
    const queryClient = useQueryClient();
  
    return useMutation({
      mutationFn: async (data: ProjectMember) => {
        const result = await projectMemberRepository.create(data);
        return result;
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["projectMembers", {}] });
      },
      onError: (error: any) => {
        if (error instanceof AxiosError) {
          if (error.response?.data?.metadata?.errors) {
            const errors = error.response.data.metadata.errors;
            logger.error(errors[0], error.response?.data.metadata);
            console.log("error axios", errors);
  
            // Cria um objeto de erro com o array de erros como propriedade
            const customError = new Error("Erro na criação de projectMember");
            (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
            throw customError;
          } else {
            logger.error("Erro desconhecido da API", error.response?.data);
            throw new Error("Erro desconhecido da API");
          }
        } else {
          logger.error("Erro desconhecido", error);
          throw error;
        }
      },
    });
  };


  export const useUpdateProjectMemberMutation = () => {
    const logger = Logger.getInstance();
    const queryClient = useQueryClient();
  
    return useMutation({
      mutationFn: async (data: ProjectMember) => {
        const result = await projectMemberRepository.update(data);
        return result;
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["projectMembers", {}] });
      },
      onError: (error: any) => {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      },
    });
  };


  export const useDeleteProjectMemberMutation = () => {
    const logger = Logger.getInstance();
    const queryClient = useQueryClient();
  
    return useMutation({
      mutationFn: async (data: ProjectMember) => {
        if (!data || !data.id) throw new Error("ProjectMember ID inválido");
        const result = await projectMemberRepository.delete(data.id);
        return result;
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["projectMembers", {}] });
      },
      onError: (error: any) => {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      },
    });
  };
